import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Route in place when the service worker relays a notification click.
 *
 * sw.js focuses a live window and asks it to navigate itself, so tapping a GG push
 * on an installed PWA lands in the conversation instead of wherever the app was last
 * left. We answer `{ handled: true }` on the message port; if nothing answers, the
 * worker falls back to a hard navigation (which reloads the SPA).
 *
 * Mounted inside ProtectedLayout on purpose: only a logged-in app can route itself.
 * On the login screen no one replies, the worker hard-navigates to the deep link, and
 * the auth guard carries the target through the login (see ProtectedLayout).
 */
export function usePushNavigation(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    function onMessage(event: MessageEvent) {
      const data = event.data;
      if (!data || data.type !== "NAVIGATE" || typeof data.url !== "string") return;

      let target: URL;
      try {
        target = new URL(data.url, window.location.origin);
      } catch {
        return;
      }
      // Never follow a URL pointing off our own origin.
      if (target.origin !== window.location.origin) return;

      navigate(target.pathname + target.search);
      event.ports?.[0]?.postMessage({ handled: true });
    }

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
}
